import { z } from 'zod';
import { format, parseISO } from 'date-fns';

import { PUT_API_MODULE_NAMES } from '@enroller/utils';
import {
  API_FIELDS_APPLICATION as APPLICATION,
  API_FIELDS_APPLICATION_LANGUAGE as LANGUAGE,
  PUT_API_FIELDS_APPLICATION as PUT_APPLICATION,
} from '@enroller/admissions-components';

import { getDependentFieldOptionalSchema, getDependentFieldSchema } from '../../../constants/sharedSchemas';
import { PageResponseData } from '../queries/graphqlResponse';
import { StudentApplicationValues } from '../StudentApplication';

export enum SCHOOL_LANGUAGES_PAGE_SECTIONS {
  school = 'school',
  languages = 'languages',
}

export const schoolAndLanguagesSchema = {
  required: z.object({
    [SCHOOL_LANGUAGES_PAGE_SECTIONS.school]: z.object({
      [APPLICATION.typeOfSchool]: getDependentFieldSchema('other', 'Other'),
      [APPLICATION.gradeLevel]: z.string().min(1, 'Current grade level is required'),
      [APPLICATION.highestGradLevelSchool]: z.string().min(1, 'Highest grade level is required'),
      [APPLICATION.graduationSituation]: z.string().min(1, 'Please specify'),
      [APPLICATION.graduationDate]: z.date({ required_error: 'Graduation date is required' }),
    }),
    [SCHOOL_LANGUAGES_PAGE_SECTIONS.languages]: z.object({
      [APPLICATION.yearsOfEnglish]: z.string().min(1, 'This field is required'),
      [APPLICATION.nativeLanguage]: z.string().min(1, 'Native language is required'),
      [PUT_APPLICATION.languageListPUT]: z.array(
        z.object({
          [LANGUAGE.language]: z.string().optional(),
          [LANGUAGE.numberOfYears]: z.string().optional(),
        }),
      ),
    }),
  }),
  optional: z.object({
    [SCHOOL_LANGUAGES_PAGE_SECTIONS.school]: z.object({
      [APPLICATION.typeOfSchool]: getDependentFieldOptionalSchema('other'),
      [APPLICATION.gradeLevel]: z.string().optional(),
      [APPLICATION.highestGradLevelSchool]: z.string().optional(),
      [APPLICATION.graduationSituation]: z.string().optional(),
      [APPLICATION.graduationDate]: z.date().optional(),
    }),
    [SCHOOL_LANGUAGES_PAGE_SECTIONS.languages]: z.object({
      [APPLICATION.yearsOfEnglish]: z.string().optional(),
      [APPLICATION.nativeLanguage]: z.string().optional(),
      [PUT_APPLICATION.languageListPUT]: z.array(
        z.object({
          [LANGUAGE.language]: z.string().optional(),
          [LANGUAGE.numberOfYears]: z.string().optional(),
        }),
      ),
    }),
  }),
};

export type SchoolAndLanguagesFormValues = z.infer<(typeof schoolAndLanguagesSchema)['required']> | z.infer<(typeof schoolAndLanguagesSchema)['optional']>;

export const defaultValues: SchoolAndLanguagesFormValues = {
  [SCHOOL_LANGUAGES_PAGE_SECTIONS.school]: {
    [APPLICATION.typeOfSchool]: { value: '', other: '' },
    [APPLICATION.gradeLevel]: '',
    [APPLICATION.highestGradLevelSchool]: '',
    [APPLICATION.graduationSituation]: '',
    [APPLICATION.graduationDate]: undefined,
  },
  [SCHOOL_LANGUAGES_PAGE_SECTIONS.languages]: {
    [APPLICATION.yearsOfEnglish]: '',
    [APPLICATION.nativeLanguage]: '',
    [PUT_APPLICATION.languageListPUT]: [
      {
        [LANGUAGE.language]: '',
        [LANGUAGE.numberOfYears]: '',
      },
    ],
  },
};

export const createStudentApplicationPayload = (values: SchoolAndLanguagesFormValues, modulesID: StudentApplicationValues['modulesID']) => {
  const {
    [APPLICATION.typeOfSchool]: typeOfSchool,
    [APPLICATION.graduationDate]: graduationDate,
    ...schoolSection
  } = values[SCHOOL_LANGUAGES_PAGE_SECTIONS.school];

  const schoolType = typeOfSchool?.value || '';
  const otherTypeOfSchool = typeOfSchool?.other || '';
  const languages = values[SCHOOL_LANGUAGES_PAGE_SECTIONS.languages];
  const otherLanguages = languages[PUT_APPLICATION.languageListPUT]?.filter((field) => {
    return field[LANGUAGE.language] && field[LANGUAGE.numberOfYears];
  });

  return {
    data: {
      [PUT_API_MODULE_NAMES.STUDENTS_APPLICATIONS]: [
        {
          [APPLICATION.ID]: modulesID[PUT_API_MODULE_NAMES.STUDENTS_APPLICATIONS],
          [APPLICATION.typeOfSchool]: schoolType,
          [APPLICATION.typeOfSchoolOther]: otherTypeOfSchool,
          [APPLICATION.graduationDate]: graduationDate ? format(graduationDate, 'yyyy-MM-dd') : '',
          ...schoolSection,
          [APPLICATION.nativeLanguage]: languages[APPLICATION.nativeLanguage] ?? '',
          [APPLICATION.yearsOfEnglish]: languages[APPLICATION.yearsOfEnglish] ?? '',
          [PUT_APPLICATION.languageListPUT]: otherLanguages,
        },
      ],
    },
  };
};

export const createSchoolAndLanguagesResponse = (studentApplication?: PageResponseData['schoolAndLanguages']): SchoolAndLanguagesFormValues => {
  if (!studentApplication) {
    console.warn('Error creating School and Languages response: studentApplication is undefined');

    return defaultValues;
  }

  try {
    const graduationDate = studentApplication[APPLICATION.graduationDate]?.value;

    return {
      [SCHOOL_LANGUAGES_PAGE_SECTIONS.school]: {
        [APPLICATION.typeOfSchool]: {
          value: studentApplication[APPLICATION.typeOfSchool]?.value || '',
          other: studentApplication[APPLICATION.typeOfSchoolOther]?.value || '',
        },
        [APPLICATION.gradeLevel]: studentApplication[APPLICATION.gradeLevel]?.value || '',
        [APPLICATION.highestGradLevelSchool]: studentApplication[APPLICATION.highestGradLevelSchool]?.value || '',
        [APPLICATION.graduationSituation]: studentApplication[APPLICATION.graduationSituation]?.value || '',
        [APPLICATION.graduationDate]: graduationDate ? parseISO(graduationDate) : undefined,
      },
      [SCHOOL_LANGUAGES_PAGE_SECTIONS.languages]: {
        [APPLICATION.yearsOfEnglish]: `${studentApplication[APPLICATION.yearsOfEnglish]?.value}` || '',
        [APPLICATION.nativeLanguage]: studentApplication[APPLICATION.nativeLanguage]?.value || '',
        [PUT_APPLICATION.languageListPUT]:
          // because the languageList field name for GET and PUT API is different
          studentApplication[APPLICATION.languageListGET]?._data?.map((lang) => ({
            [LANGUAGE.id]: lang?.id?.value || '',
            [LANGUAGE.language]: lang?.[LANGUAGE.language]?.value || '',
            [LANGUAGE.numberOfYears]: String(lang?.[LANGUAGE.numberOfYears]?.value) || '',
          })) || [],
      },
    };
  } catch (e) {
    console.error('Error creating School and Languages response:', e);
    return defaultValues;
  }
};
